/* eslint-disable no-console */

import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  BaseSignalRMessage,
  ConversationSignalRMessage,
  SignalRFilterKey,
} from '../../../hooks/use-signalr';
import { useAppContext } from '../../context/app-context';
import {
  ConversationItemDataFragment,
  Gender,
  GetConversationItemsDocument,
  GetConversationItemsQueryVariables,
  Language,
  MfaAuthenticationType,
  PatientStatus,
  PushType,
  UserType,
} from '../../types/api/graphql';

export function useConversationItems(
  variables: GetConversationItemsQueryVariables,
  disableRealtimeUpdates: boolean = false
) {
  const [items, setItems] = useState<
    Map<string, ConversationItemDataFragment[]>
  >(new Map<string, ConversationItemDataFragment[]>());

  const [filterItems, setFilterItems] = useState<
    ConversationItemDataFragment[] | null
  >(null);

  // get data based on healthprovider
  const {
    data: conversationItemsData,
    loading: conversationItemsLoading,
    refetch,
  } = useQuery(GetConversationItemsDocument, {
    variables: {
      ...variables,
      search: variables.search!.length > 2 ? variables.search : '',
    },
    fetchPolicy: 'network-only',
    skip: !variables.healthProviderId,
  });

  const data = useMemo(() => {
    // directly return filter items
    if (filterItems) return filterItems;

    // get stored items based on healthprovider
    const storedItems = items.get(variables.healthProviderId) ?? [];

    // exit
    if (!storedItems.length) return [];

    // filter based on health professional
    return storedItems
      .filter(
        x =>
          !variables.healthProfessionalId ||
          x.healthProfessional?.id === variables.healthProfessionalId
      )
      .sort(
        (a, b) =>
          new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
      );
  }, [
    filterItems,
    items,
    variables.healthProfessionalId,
    variables.healthProviderId,
  ]);

  // Signal R
  const { signalRMessage, addFilter } = useAppContext();

  const initialized = useRef(false);

  useEffect(() => {
    // skip if loading
    if (conversationItemsLoading) {
      // reset initialized if loading
      initialized.current = false;
      return;
    }

    // skip if filters are enabled
    if (
      variables.startDate ||
      variables.endDate ||
      (variables.search && variables.search.length > 2)
    ) {
      setFilterItems(conversationItemsData?.conversations ?? []);
      return;
    }

    // reset filter items
    setFilterItems(null);

    // skip if allready has data
    if (!initialized.current) {
      initialized.current = true;
    } else {
      console.log(
        `[${SignalRFilterKey.CONVERSATIONS}][${variables.healthProviderId}] return allready have initial items`
      );
      return;
    }

    // skip if no items
    if (!conversationItemsData?.conversations?.length) {
      const emptyMap = new Map();
      emptyMap.set(variables.healthProviderId, []);
      setItems(emptyMap);
      return;
    }

    // update items
    setItems(prevItems => {
      const newItems = new Map(prevItems);

      // key does not exists so fill items
      if (!newItems.has(variables.healthProviderId)) {
        console.log(
          `[${SignalRFilterKey.CONVERSATIONS}][${variables.healthProviderId}] items initialize ${conversationItemsData.conversations.length}`
        );
        newItems.set(
          variables.healthProviderId,
          conversationItemsData.conversations
        );
      } else {
        // key exists so add or update items
        console.log(
          `[${SignalRFilterKey.CONVERSATIONS}][${variables.healthProviderId}] update items ${conversationItemsData.conversations.length}`
        );
        newItems.set(
          variables.healthProviderId,
          conversationItemsData.conversations.reduce(
            (acc, newItem) => {
              const exists = acc.some(
                existingItem => existingItem.id === newItem.id
              );

              if (exists) {
                // If the item already exists, update it
                return acc.map(existingItem => {
                  if (existingItem.id === newItem.id) {
                    return { ...existingItem, ...newItem };
                  }
                  return existingItem;
                });
              }
              // If the item doesn't exist, add it
              return [...acc, newItem];
            },
            [...(newItems.get(variables.healthProviderId) ?? [])]
          )
        );
      }
      return newItems;
    });
  }, [
    conversationItemsData?.conversations,
    conversationItemsLoading,
    variables.endDate,
    variables.healthProfessionalId,
    variables.healthProviderId,
    variables.search,
    variables.startDate,
  ]);

  const previousConversationMessage = useRef<ConversationSignalRMessage | null>(
    null
  );

  const filter = useCallback(
    (message: BaseSignalRMessage) => {
      if (
        message &&
        [
          PushType.ConversationCreated,
          PushType.ConversationDeleted,
          PushType.ConversationUpdated,
        ].includes(message.type)
      ) {
        const { healthProviderId, uniqueId } =
          message as ConversationSignalRMessage;
        if (
          healthProviderId === variables.healthProviderId &&
          uniqueId !== previousConversationMessage.current?.uniqueId
        ) {
          return true;
        }
      }
      return false;
    },
    [variables.healthProviderId]
  );

  useEffect(() => {
    if (conversationItemsLoading) return;
    if (conversationItemsData && variables.healthProviderId) {
      addFilter({ key: SignalRFilterKey.CONVERSATIONS, filter });
    }
  }, [
    addFilter,
    conversationItemsData,
    conversationItemsLoading,
    filter,
    variables.healthProviderId,
  ]);

  useEffect(() => {
    if (!signalRMessage || !filter(signalRMessage)) return;
    const message = signalRMessage as ConversationSignalRMessage;
    const { type, conversationId, healthProfessionalId } = message;

    // keep track of last conversation message
    previousConversationMessage.current = message;

    // realtime updates disables

    if (disableRealtimeUpdates) {
      console.log(
        `[${SignalRFilterKey.CONVERSATIONS}] realtime updates disabled`
      );
      return;
    }

    // remove item if health professional id is different
    if (
      variables.healthProfessionalId &&
      variables.healthProfessionalId !== healthProfessionalId
    ) {
      setItems(prevItems => {
        const newItems = new Map(prevItems);
        const currentItems = newItems.get(variables.healthProviderId) ?? [];
        // Filter out the deleted conversation
        const updatedItems = currentItems.filter(
          item => item.id !== conversationId
        );

        // Update the Map with the filtered items
        newItems.set(variables.healthProviderId, updatedItems);

        return newItems;
      });
      return;
    }

    // handle conversation deleted by removing it from the local state
    if (type === PushType.ConversationDeleted) {
      console.log(
        `[${SignalRFilterKey.CONVERSATIONS}] deleted remove conversation ${conversationId} from cache`
      );

      setItems(prevItems => {
        const newItems = new Map(prevItems);
        const currentItems = newItems.get(variables.healthProviderId) ?? [];

        // Filter out the deleted conversation
        const updatedItems = currentItems.filter(
          item => item.id !== conversationId
        );

        // Update the Map with the filtered items
        newItems.set(variables.healthProviderId, updatedItems);

        return newItems;
      });
    } else if (
      type === PushType.ConversationCreated ||
      type === PushType.ConversationUpdated
    ) {
      console.log(
        `[${SignalRFilterKey.CONVERSATIONS}] ${
          type === PushType.ConversationCreated ? 'adding' : 'updating'
        } conversation ${conversationId}`
      );

      // Extract all relevant conversation data from the message
      const {
        conversationId: id,
        healthProfessionalInitials,
        patientFirstName,
        patientLastName,
        patientBirthDate,
        patientDutchInsurance,
        patientGender,
        isNew,
        lastUpdated,
        noReply,
        status,
        category,
        urgent,
      } = message;

      setItems(prevItems => {
        const newItems = new Map(prevItems);
        const currentItems = newItems.get(variables.healthProviderId) ?? [];

        // Create or update the conversation item
        const conversationItem: ConversationItemDataFragment = {
          id,
          title: '',
          healthProfessional: healthProfessionalId
            ? {
                firstName: '',
                lastName: '',
                gender: Gender.Male,
                id: healthProfessionalId,
                initials: healthProfessionalInitials ?? '',
                __typename: 'BaseUser',
                type: UserType.HealthProfessional, // Replace 'SomeEnumValue' with the actual enum value
              }
            : null,
          patient: {
            firstName: patientFirstName,
            lastName: patientLastName,
            birthDate: patientBirthDate,
            gender: patientGender,
            __typename: 'Patient',
            id: undefined,
            email: '',
            language: Language.En,
            postalCode: '',
            dutchInsurance: patientDutchInsurance,
            app: false,
            status: PatientStatus.Active,
            noShowProtocol: false,
            mfaAuthenticationType: MfaAuthenticationType.Sms,
          },
          isNew,
          lastUpdated,
          noReply,
          status,
          category,
          urgent,
          __typename: 'Conversation',
        };

        // Find if the conversation already exists
        const existingIndex = currentItems.findIndex(item => item.id === id);

        if (existingIndex >= 0) {
          // Update existing conversation
          const updatedItems = [...currentItems];
          updatedItems[existingIndex] = {
            ...updatedItems[existingIndex],
            ...conversationItem,
          };
          newItems.set(variables.healthProviderId, updatedItems);
        } else {
          // Add new conversation
          newItems.set(variables.healthProviderId, [
            ...currentItems,
            conversationItem,
          ]);
        }

        return newItems;
      });
    }
  }, [
    disableRealtimeUpdates,
    filter,
    items,
    refetch,
    signalRMessage,
    variables,
  ]);

  return {
    data: data ?? [],
    loading: conversationItemsLoading,
  };
}
